import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api.service';

export interface HospitalAccessCandidate {
  _id: string;
  firstName: string;
  lastName: string;
  practiceName: string;
  hospitalCode: string;
  staffRole: string;
  email: string;
}

/**
 * API service for hospital multi-site access management.
 * Handles hospital search, request submission, admin actions, and site listing.
 */
@Injectable({
  providedIn: 'root'
})
export class HospitalAccessService {

  /** Pending request count for sidebar badge — shared across components */
  private pendingCount$ = new BehaviorSubject<number>(0);
  readonly pendingCountObs$ = this.pendingCount$.asObservable();

  constructor(private api: ApiService) {}

  /** Refresh pending count from backend and update shared state */
  refreshPendingCount(): void {
    this.getPendingCount().subscribe({
      next: (res: any) => {
        this.pendingCount$.next(res?.data?.count || 0);
      }
    });
  }

  // ─── PCV/VC USER ENDPOINTS ─────────────────────────────────────────

  /** Search hospitals by name or code (typeahead) */
  searchHospitals(query: string): Observable<any> {
    return this.api.apiRequest('get', `hospital-access/search?q=${encodeURIComponent(query)}`, {});
  }

  /** Submit a new access request */
  submitRequest(hospitalCode: string): Observable<any> {
    return this.api.apiRequest('post', 'hospital-access/request', { hospitalCode });
  }

  /** Get user's accessible sites (primary + secondary + pending) */
  getMySites(): Observable<any> {
    return this.api.apiRequest('get', 'hospital-access/my-sites', {});
  }

  /** User removes their own secondary access */
  selfRemoveAccess(requestId: string): Observable<any> {
    return this.api.apiRequest('post', `hospital-access/${requestId}/self-remove`, {});
  }

  /** Get requests for hospital admin's accessible sites */
  getRequestsForSite(page?: number, limit?: number, hospitalCode?: string): Observable<any> {
    const params: string[] = [];
    if (page) params.push(`page=${page}`);
    if (limit) params.push(`limit=${limit}`);
    if (hospitalCode) params.push(`hospitalCode=${encodeURIComponent(hospitalCode)}`);
    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return this.api.apiRequest('get', `hospital-access/requests-for-site${queryString}`, {});
  }

  /** Search existing VC accounts before (platform admin) or after (hospital admin) choosing a site. */
  searchGrantCandidates(query: string, hospitalCode?: string): Observable<any> {
    const siteParam = hospitalCode ? `hospitalCode=${encodeURIComponent(hospitalCode)}&` : '';
    return this.api.apiRequest(
      'get',
      `hospital-access/admin/candidates?${siteParam}q=${encodeURIComponent(query)}`,
      {}
    );
  }

  /** Immediately grant an existing VC account access to a managed site. */
  directGrant(targetUserId: string, hospitalCode: string): Observable<any> {
    return this.api.apiRequest('post', 'hospital-access/admin/direct-grant', {
      targetUserId,
      hospitalCode
    });
  }

  // ─── ADMIN ENDPOINTS ───────────────────────────────────────────────

  /** Search destination sites for a platform-admin direct grant. */
  searchAdminHospitals(query: string): Observable<any> {
    return this.api.apiRequest('get', `hospital-access/admin/hospitals?q=${encodeURIComponent(query)}`, {});
  }

  /** Get all requests with filters (mission admin) */
  getAdminRequests(params: {
    status?: string;
    hospitalCode?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  } = {}): Observable<any> {
    const queryParts: string[] = [];
    if (params.status) queryParts.push(`status=${params.status}`);
    if (params.hospitalCode) queryParts.push(`hospitalCode=${encodeURIComponent(params.hospitalCode)}`);
    if (params.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
    if (params.dateFrom) queryParts.push(`dateFrom=${params.dateFrom}`);
    if (params.dateTo) queryParts.push(`dateTo=${params.dateTo}`);
    if (params.page) queryParts.push(`page=${params.page}`);
    if (params.limit) queryParts.push(`limit=${params.limit}`);

    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    return this.api.apiRequest('get', `hospital-access/admin/requests${queryString}`, {});
  }

  /** Get unread pending count for sidebar badge */
  getPendingCount(): Observable<any> {
    return this.api.apiRequest('get', 'hospital-access/admin/pending-count', {});
  }

  /** Approve a request */
  approveRequest(requestId: string): Observable<any> {
    return this.api.apiRequest('post', `hospital-access/admin/${requestId}/approve`, {});
  }

  /** Reject a request (requires reason) */
  rejectRequest(requestId: string, reason: string): Observable<any> {
    return this.api.apiRequest('post', `hospital-access/admin/${requestId}/reject`, { reason });
  }

  /** Revoke approved access */
  revokeAccess(requestId: string): Observable<any> {
    return this.api.apiRequest('post', `hospital-access/admin/${requestId}/revoke`, {});
  }

  /** Undo rejection (within 3-month window) */
  undoRejection(requestId: string): Observable<any> {
    return this.api.apiRequest('post', `hospital-access/admin/${requestId}/undo-rejection`, {});
  }

  /** Re-approve revoked access */
  reApproveRequest(requestId: string): Observable<any> {
    return this.api.apiRequest('post', `hospital-access/admin/${requestId}/re-approve`, {});
  }

  /** Mark requests as read */
  markRequestsRead(requestIds?: string[]): Observable<any> {
    return this.api.apiRequest('post', 'hospital-access/admin/mark-read', { requestIds });
  }
}
